import { Check } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FormSchemaProvider } from '@procureai/ui-form-mui';
import { useUuid } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered.tsx';
import { SupplierContactEditForm } from '~/routes/IntakeDetail/components/SupplierContactEditDialog/components/SupplierContactEditForm/SupplierContactEditForm.tsx';

import {
  SupplierContactData,
  supplierContactSchema,
  supplierContactDataDefault,
} from './components/SupplierContactEditForm/SupplierContactEditForm.schema.ts';
import { useSupplierContactEditBackend } from './hooks/useSupplierContactEditBackend.ts';

export interface SupplierContactEditDialogProps {
  contactId?: string | undefined;
  supplierId: string;
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: (data: SupplierContactData) => void;
}

export const SupplierContactEditDialog = ({
  contactId,
  supplierId,
  onClose,
  onSaveSuccess,
  ...otherProps
}: SupplierContactEditDialogProps) => {
  const formId = useUuid();
  const { t } = useTranslation(undefined, { keyPrefix: 'SupplierContactEditDialog' });
  const { data, save, saving, loading } = useSupplierContactEditBackend({ contactId });

  const defaultValues = useMemo(() => {
    return supplierContactDataDefault({ supplierId });
  }, [supplierId]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} {...otherProps}>
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent>
        <FormSchemaProvider<SupplierContactData>
          id={formId}
          data={data ?? undefined}
          schema={supplierContactSchema}
          defaultValues={defaultValues}
          onSubmit={async (data) => {
            const success = await save(data);
            if (success) {
              onSaveSuccess?.(data);
              onClose();
            }
          }}
          stopPropagation
        >
          <SupplierContactEditForm />
        </FormSchemaProvider>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose}>
          {t('cancel.label')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          loadingPosition="start"
          form={formId}
          loading={saving}
          disabled={loading}
          startIcon={<Check />}
        >
          {t('confirm.label')}
        </Button>
      </DialogActions>
      {loading && <CircularProgressCentered />}
    </Dialog>
  );
};
